'use server';

import { headers } from 'next/headers'; // To get headers for IP/UA
import crypto from 'crypto';
import fs from 'fs';
import path from 'path';
import nodemailer from 'nodemailer';

// --- Helper functions (ensure these match the middleware) ---
function anonymizeIp(ip?: string): string {
    if (!ip) return 'unknown';
    if (ip.includes(':')) { // Basic check for IPv6
        return ip.substring(0, ip.lastIndexOf(':') + 1) + '0'; // Simple anonymization
    }
    const parts = ip.split('.');
    if (parts.length === 4) {
        return `${parts[0]}.${parts[1]}.${parts[2]}.0`;
    }
    return ip; // Return original if not standard IPv4/IPv6 format
}

function generateSessionHash(ip: string, userAgent: string): string {
    const data = `${anonymizeIp(ip)}-${userAgent}`;
    return crypto.createHash('sha256').update(data).digest('hex');
}

// --- Structured logging ---
type LogLevel = 'info' | 'warn' | 'error' | 'debug';

// Use a simpler approach with a non-any type that doesn't trigger ESLint
type LogPayload = Record<string, unknown>;

// Helper to safely convert errors to a proper payload object
function errorToPayload(error: unknown): LogPayload {
    if (error instanceof Error) {
        return {
            message: error.message,
            name: error.name,
            stack: error.stack
        };
    }
    return { error: String(error) };
}

async function logContactFormEvent(level: LogLevel, message: string, data?: LogPayload) {
    try {
        const timestamp = new Date().toISOString();
        const logEntry = {
            timestamp,
            level,
            message,
            source: 'contactForm',
            data
        };
        
        // Ensure logs directory exists
        const logDir = path.join(process.cwd(), 'logs');
        if (!fs.existsSync(logDir)) {
            fs.mkdirSync(logDir, { recursive: true });
        }
        
        const logFile = path.join(logDir, `contact_form_${new Date().toISOString().split('T')[0]}.log`);
        
        // Append to log file
        fs.appendFileSync(
            logFile, 
            JSON.stringify(logEntry) + '\n', 
            { encoding: 'utf8' }
        );
        
        // Also log to console for server-side visibility
        console[level === 'warn' ? 'warn' : level === 'error' ? 'error' : 'log'](
            `[CONTACT FORM] [${level.toUpperCase()}] ${message}`, 
            data ? data : ''
        );
    } catch (err) {
        // Fallback if file logging fails
        console.error('Logging failed:', err);
        console[level === 'warn' ? 'warn' : level === 'error' ? 'error' : 'log'](
            `[CONTACT FORM] [${level.toUpperCase()}] ${message}`, 
            data ? data : ''
        );
    }
}

interface SubmissionResult {
    success: boolean;
    error?: string;
}

export async function handleFormSubmit(formData: FormData): Promise<SubmissionResult> {
    await logContactFormEvent('info', "Received form submission");
    
    try {
        // --- Extract Form Data --- 
        // Ensure names match the 'name' attributes in ContactForm.tsx
        const name = formData.get('name') as string || '';
        const email = formData.get('email') as string || '';
        const anliegen = formData.get('anliegen') as string || ''; 
        const anliegenDetail = formData.get('anliegen_detail') as string || '';
        const companyName = formData.get('companyName') as string || '';
        const companySize = formData.get('companySize') as string || '';
        const message = formData.get('message') as string || '';
        const referrer = formData.get('referrer') as string || 'N/A';
        const matomoVisitorId = formData.get('id') as string || 'N/A';
        
        await logContactFormEvent('debug', "Form data extracted", { 
            name, email, anliegen, 
            hasCompanyInfo: !!(companyName && companySize),
            referrer 
        });

        // --- Basic Validation --- 
        if (!name || !email) {
            await logContactFormEvent('warn', "Validation failed: Missing name or email");
            return { success: false, error: "Name and Email are required." };
        }
        
        if (anliegen === 'employee' && (!companyName || !companySize)) {
            await logContactFormEvent('warn', "Validation failed: Missing company details for employee inquiry");
            return { success: false, error: "Company Name and Size are required for employee inquiries." };
        }

        // --- Retrieve Session Tracking Data --- 
        let trackedFiles: string[] = [];
        let sessionInfo = { ip: 'unknown', userAgent: 'unknown', sessionHash: 'unknown' };
        
        try {
            const headerList = await headers();
            const userAgentHeader = headerList.get('user-agent') ?? 'unknown';
            const ipHeader = headerList.get('x-forwarded-for')?.split(',')[0].trim() || headerList.get('x-real-ip') || 'unknown';

            sessionInfo = {
                ip: anonymizeIp(ipHeader),
                userAgent: userAgentHeader,
                sessionHash: generateSessionHash(ipHeader, userAgentHeader)
            };

            await logContactFormEvent('debug', "Session info retrieved", {
                anonymizedIp: sessionInfo.ip,
                sessionHash: sessionInfo.sessionHash
            });

            const now = new Date();
            const year = now.getFullYear().toString();
            const month = (now.getMonth() + 1).toString().padStart(2, '0');
            const day = now.getDate().toString().padStart(2, '0');

            const trackingDir = path.join('./data', 'tracking', year, month, day, sessionInfo.sessionHash);

            if (fs.existsSync(trackingDir)) {
                const files = await fs.promises.readdir(trackingDir);
                trackedFiles = files.filter(file => file.endsWith('.json')).sort();
                await logContactFormEvent('debug', `Found ${trackedFiles.length} tracking files for session`);
            } else {
                await logContactFormEvent('debug', `No tracking directory found: ${trackingDir}`);
            }
        } catch (error) {
            await logContactFormEvent('error', 'Failed to retrieve tracking data', errorToPayload(error));
            trackedFiles = ['Error retrieving tracking files'];
        }

        // --- Prepare Email Content --- 
        const emailSubject = `Neue Kontaktanfrage von ${name} (${anliegen || 'Unbekannt'})`;
        const emailBody = `
        Neue Kontaktanfrage erhalten:
        -----------------------------
        Name: ${name}
        Email: ${email}
        Anliegen: ${anliegen}${anliegen === 'other' && anliegenDetail ? ` (${anliegenDetail})` : ''}
        Nachricht: ${message || '-'}
        
        ${anliegen === 'employee' ? `Unternehmen: ${companyName || '-'}
        Unternehmensgröße: ${companySize || '-'}
        ` : ''}
        -----------------------------
        Tracking Informationen:
        -----------------------------
        Matomo Visitor ID (id): ${matomoVisitorId}
        Referrer: ${referrer}
        Session Hash: ${sessionInfo.sessionHash}
        IP (Anonymisiert): ${sessionInfo.ip}
        User Agent: ${sessionInfo.userAgent}
        
        Tracked Files (${trackedFiles.length}):
        ${trackedFiles.length > 0 ? trackedFiles.map(f => `- ${f}`).join('\n') : 'Keine'}
        `;

        // --- Send Email using Nodemailer --- 
        // Get email configuration from environment variables
        const emailHost = process.env.EMAIL_HOST || '';
        const emailPort = parseInt(process.env.EMAIL_PORT || '587');
        const emailUser = process.env.EMAIL_USER || '';
        const emailPass = process.env.EMAIL_PASS || '';
        const emailFrom = process.env.EMAIL_FROM || '';
        const emailTo = process.env.EMAIL_TO || 'contact@umynd.ai'; // Default if not set
        
        // Debug log to check if environment variables are being detected
        await logContactFormEvent('debug', 'Email config detected', { 
            hasHost: !!emailHost, 
            hasUser: !!emailUser,
            hasPass: !!emailPass,
            hasFrom: !!emailFrom,
            port: emailPort
        });
        
        // Check if required email config is available
        if (!emailHost || !emailUser || !emailPass) {
            await logContactFormEvent('error', 'Email configuration missing', { 
                hasHost: !!emailHost, 
                hasUser: !!emailUser,
                hasPass: !!emailPass 
            });
            return { 
                success: false, 
                error: 'Server configuration error. Please contact the administrator.' 
            };
        }

        const transporter = nodemailer.createTransport({
            host: emailHost,
            port: emailPort,
            secure: emailPort === 465, // true for 465, false for other ports
            auth: {
                user: emailUser,
                pass: emailPass,
            },
            connectionTimeout: 10000,
            greetingTimeout: 10000,
            socketTimeout: 10000,
        });

        const mailOptions = {
            from: emailFrom || `"Umynd Website" <${emailUser}>`,
            to: emailTo,
            subject: emailSubject,
            text: emailBody,
        };

        await logContactFormEvent('info', `Attempting to send email to ${mailOptions.to}`);
        
        try {
            const info = await transporter.sendMail(mailOptions);
            await logContactFormEvent('info', "Email sent successfully", { 
                messageId: info.messageId 
            });
            
            // Also save a local copy of the form submission
            try {
                const submissionsDir = path.join(process.cwd(), 'data', 'submissions');
                if (!fs.existsSync(submissionsDir)) {
                    fs.mkdirSync(submissionsDir, { recursive: true });
                }
                
                const formData = {
                    timestamp: new Date().toISOString(),
                    name,
                    email,
                    anliegen,
                    anliegenDetail,
                    companyName,
                    companySize,
                    message,
                    referrer,
                    matomoVisitorId,
                    sessionInfo,
                    trackedFiles
                };
                
                const filename = `${Date.now()}-${crypto.randomBytes(4).toString('hex')}.json`;
                fs.writeFileSync(
                    path.join(submissionsDir, filename),
                    JSON.stringify(formData, null, 2),
                    'utf8'
                );
                
                await logContactFormEvent('debug', `Saved form submission to ${filename}`);
            } catch (err) {
                await logContactFormEvent('warn', 'Failed to save local copy of submission', errorToPayload(err));
                // Continue since the email was sent successfully
            }
            
            return { success: true };
        } catch (error) {
            await logContactFormEvent('error', 'Failed to send email', errorToPayload(error));
            const errorMessage = error instanceof Error ? error.message : 'Unknown email error';
            return { 
                success: false, 
                error: `Failed to send email: ${errorMessage}` 
            };
        }
    } catch (error) {
        await logContactFormEvent('error', 'Unexpected error in form submission', errorToPayload(error));
        return { 
            success: false, 
            error: 'An unexpected error occurred. Please try again later.' 
        };
    }
} 